import React, { useState } from 'react';
import { LadderOfInference } from '../components/LadderOfInference';
import {
  Monitor,
  Smartphone,
  FileText,
  CreditCard,
  Maximize2,
  ArrowUp
} from 'lucide-react';

export const LadderOfInferenceDemo: React.FC = () => {
  const [selectedFormat, setSelectedFormat] = useState<'slide' | 'web' | 'mobile' | 'print' | 'field-card' | 'poster'>('web');
  const [selectedVariant, setSelectedVariant] = useState<'vertical' | 'horizontal' | 'circular' | 'journey'>('vertical');
  const [contentDensity, setContentDensity] = useState<'minimal' | 'standard' | 'detailed'>('standard');

  const formats = [
    { id: 'slide', name: 'PowerPoint Slide', icon: <Monitor className="h-5 w-5" />, description: 'Presentation-ready format' },
    { id: 'web', name: 'Web Page', icon: <Monitor className="h-5 w-5" />, description: 'Interactive web experience' },
    { id: 'mobile', name: 'Mobile App', icon: <Smartphone className="h-5 w-5" />, description: 'Touch-optimized interface' },
    { id: 'print', name: 'Print Document', icon: <FileText className="h-5 w-5" />, description: 'High-resolution print format' },
    { id: 'field-card', name: 'Field Card', icon: <CreditCard className="h-5 w-5" />, description: 'Quick reference card' },
    { id: 'poster', name: 'Poster', icon: <Maximize2 className="h-5 w-5" />, description: 'Large format display' }
  ];

  const variants = [
    { id: 'vertical', name: 'Vertical Ladder', description: 'Traditional ladder format' },
    { id: 'horizontal', name: 'Horizontal Flow', description: 'Process flow format' },
    { id: 'circular', name: 'Circular Cycle', description: 'Reflective learning cycle' },
    { id: 'journey', name: 'Journey Map', description: 'Personal growth journey' }
  ];

  const densities = [
    { id: 'minimal', name: 'Minimal', description: 'Essential information only' },
    { id: 'standard', name: 'Standard', description: 'Balanced content' },
    { id: 'detailed', name: 'Detailed', description: 'Comprehensive information' }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <ArrowUp className="h-10 w-10 text-consulting-slate-blue-600" />
          <h1 className="text-4xl font-bold text-charcoal-900">Ladder of Inference</h1>
        </div>
        <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
          A format-adaptive component that demonstrates how Peak Priorities' visual grammar system can create multiple representations of the same powerful framework.
        </p>
      </div>

      {/* Format Selection */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Format Selection</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {formats.map((format) => (
            <button
              key={format.id}
              onClick={() => setSelectedFormat(format.id as any)}
              className={`p-4  border-2 transition-all text-left ${
                selectedFormat === format.id
                  ? 'border-consulting-slate-blue-600 bg-consulting-slate-blue-50 text-consulting-slate-blue-800'
                  : 'border-[var(--rule)] bg-[var(--bg-raised)] text-charcoal-700 hover:border-[var(--rule)]'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                {format.icon}
                <span className="font-semibold">{format.name}</span>
              </div>
              <p className="text-sm opacity-75">{format.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Variant Selection */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Visual Variant</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {variants.map((variant) => (
            <button
              key={variant.id}
              onClick={() => setSelectedVariant(variant.id as any)}
              className={`p-4  border-2 transition-all text-left ${
                selectedVariant === variant.id
                  ? 'border-sage-600 bg-sage-50 text-sage-800'
                  : 'border-[var(--rule)] bg-[var(--bg-raised)] text-charcoal-700 hover:border-[var(--rule)]'
              }`}
            >
              <div className="font-semibold mb-1">{variant.name}</div>
              <p className="text-sm opacity-75">{variant.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Content Density Selection */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Content Density</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {densities.map((density) => (
            <button
              key={density.id}
              onClick={() => setContentDensity(density.id as any)}
              className={`p-4  border-2 transition-all text-left ${
                contentDensity === density.id
                  ? 'border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)]'
                  : 'border-[var(--rule)] bg-[var(--bg-raised)] text-charcoal-700 hover:border-[var(--rule)]'
              }`}
            >
              <div className="font-semibold mb-1">{density.name}</div>
              <p className="text-sm opacity-75">{density.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Live Preview */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-charcoal-900">Live Preview</h2>
          <div className="flex items-center gap-2 text-sm text-charcoal-600">
            <span>Format: {selectedFormat}</span>
            <span>•</span>
            <span>Variant: {selectedVariant}</span>
            <span>•</span>
            <span>Density: {contentDensity}</span>
          </div>
        </div>
        
        <div className="border-2 border-dashed border-[var(--rule)] p-6 bg-[var(--surface)]">
          <LadderOfInference
            format={selectedFormat}
            variant={selectedVariant}
            contentDensity={contentDensity}
            interactive={selectedFormat === 'web'}
            showReflection={selectedFormat !== 'field-card'}
          />
        </div>
      </div>

      {/* Format Examples */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Format Examples</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {/* PowerPoint Slide */}
          <div className="p-4 border-2 border-[var(--rule)] bg-[var(--bg-raised)]">
            <h3 className="font-semibold text-charcoal-900 mb-3 text-center">PowerPoint Slide</h3>
            <div className="bg-[var(--surface)] p-4 h-80 flex items-center justify-center border border-[var(--rule)] overflow-hidden">
              <LadderOfInference
                format="slide"
                variant="vertical"
                contentDensity="minimal"
                interactive={false}
                showReflection={false}
              />
            </div>
          </div>

          {/* Field Card */}
          <div className="p-4 border-2 border-[var(--rule)] bg-[var(--bg-raised)]">
            <h3 className="font-semibold text-charcoal-900 mb-3 text-center">Field Card</h3>
            <div className="bg-[var(--surface)] p-4 h-80 flex items-center justify-center border border-[var(--rule)] overflow-hidden">
              <LadderOfInference
                format="field-card"
                variant="vertical"
                contentDensity="minimal"
                interactive={false}
                showReflection={false}
              />
            </div>
          </div>

          {/* Poster */}
          <div className="p-4 border-2 border-[var(--rule)] bg-[var(--bg-raised)]">
            <h3 className="font-semibold text-charcoal-900 mb-3 text-center">Poster</h3>
            <div className="bg-[var(--surface)] p-4 h-80 flex items-center justify-center border border-[var(--rule)] overflow-hidden">
              <LadderOfInference
                format="poster"
                variant="vertical"
                contentDensity="standard"
                interactive={false}
                showReflection={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Implementation Notes */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 border-2 border-consulting-slate-blue-200 p-8">
        <h3 className="text-2xl font-bold text-charcoal-900 mb-4">Implementation Notes</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-charcoal-900 mb-3">Format Adaptations</h4>
            <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-2">
              <li><strong>Slide:</strong> Compact, presentation-ready, minimal text</li>
              <li><strong>Web:</strong> Interactive, detailed, full functionality</li>
              <li><strong>Mobile:</strong> Touch-optimized, simplified interface</li>
              <li><strong>Print:</strong> High-resolution, print-friendly colors</li>
              <li><strong>Field Card:</strong> Essential info only, credit card size</li>
              <li><strong>Poster:</strong> Large format, visual impact, overview</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-charcoal-900 mb-3">Visual Grammar Application</h4>
            <ul className="list-disc list-inside text-charcoal-700 text-sm space-y-2">
              <li><strong>Shapes:</strong> Consistent primitives across all formats</li>
              <li><strong>Colors:</strong> Blue-to-amber progression showing subjectivity</li>
              <li><strong>Typography:</strong> Appropriate hierarchy for each context</li>
              <li><strong>Spacing:</strong> Golden ratio applied consistently</li>
              <li><strong>Interactivity:</strong> Context-appropriate engagement</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
